import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import axiosClient from '../../libs/axiosClient';
import HeaderMain from '../../components/HeaderMain/HeaderMain';
import HeaderMobile from '../../components/HeaderMobile/HeaderMobile';
import Footer from '../../components/Footer/Footer';
import ChatModal from '../../components/ChatModal/ChatModal';
import PaymentModal from '../../components/PaymentModal/PaymentModal';
import styles from './ClinicDetail.module.scss';
import detailStar from '../../assets/hospitalReservation/detailStar.png';
import detailPhone from '../../assets/hospitalReservation/detailPhone.png';

interface HospitalDetail {
  id: number;
  name: string;
  introduce: string;
  address: string;
  address_english: string;
  nearest_subway_station: string;
  operating_hours_notice: string;
  profile_image: string | null;
  tel: string;
  phone: string;
  email: string;
  status: string;
  banner_image: string[];
}

function ClinicDetail() {
  const { hospital_id } = useParams<{ hospital_id: string }>();
  const [selectedTab, setSelectedTab] = useState('consultation');
  const { t, i18n } = useTranslation();
  const [hospitalData, setHospitalData] = useState<HospitalDetail | null>(null);
  const [hospitalSchedules, setHospitalSchedules] = useState<any[]>([]);
  const [rating, setRating] = useState<{ avg_score: number; total_review_quantity: number } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [consultCount, setConsultCount] = useState<number | null>(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';

    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    const fetchHospitalDetails = async () => {
      try {
        const response = await axiosClient.get(
          `/api/v1/hospital/${hospital_id}`
        );
        setHospitalData(response.data);
        setHospitalSchedules(response.data.hospital_schedules || []);
        
        const ratingResponse = await axiosClient.get(
          `/api/v1/review/${response.data.id}/stats`
        );
        setRating({
          avg_score: ratingResponse.data.avg_score,
          total_review_quantity: ratingResponse.data.total_review_quantity,
        });
        
        const consultCountResponse = await axiosClient.get(
          `/api/v1/hospital/${response.data.id}/consult_count`
        );
        setConsultCount(consultCountResponse.data.count);
      } catch (error) {
        console.error('Error fetching hospital details:', error);
      }
    };

    fetchHospitalDetails();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [i18n]);
  
  function stripHtmlTags(html: string): string {
    return html.replace(/<[^>]*>/g, '');
  }
  
  if (!hospitalData) {
    return <div>Loading...</div>;
  }
  
  const {
    name,
    introduce,
    address,
    nearest_subway_station,
    operating_hours_notice,
    profile_image,
    tel,
    phone,
  } = hospitalData;
  
  const daysOfWeek = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];
  
  const renderSchedule = (day: string) => {
    const schedule = hospitalSchedules.find((s) => s.day_of_week === day);
  
    if (!schedule || schedule.is_closed) {
      return <div style={{ color: "red", fontWeight: "bold" }}>Closed</div>;
    }
  
    return (
      <div>
        <div>{schedule.open_time?.slice(0, 5)}</div>
        <div>-</div>
        <div>{schedule.close_time?.slice(0, 5)}</div>
      </div>
    );
  };
  
  const handleNextBanner = () => {
    if (hospitalData?.banner_image) {
      setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % hospitalData.banner_image.length);
    }
  };

  const handlePrevBanner = () => {
    if (hospitalData?.banner_image) {
      setCurrentBannerIndex((prevIndex) =>
        (prevIndex - 1 + hospitalData.banner_image.length) % hospitalData.banner_image.length
      );
    }
  };

  const bannerImages = hospitalData?.banner_image?.length
  ? hospitalData.banner_image
  : ['https://via.placeholder.com/1920x500'];
  
  return (
    <>
      {isChatModalOpen && (
        <ChatModal
          onClose={() => setIsChatModalOpen(false)}
          hospitalId={hospitalData.id.toString()}
          hospitalName={hospitalData.name.toString()}
          profileImage={hospitalData.profile_image || ""}
        />
      )}
      
      {isMobile ? (
        <HeaderMobile />
      ) : (
      <HeaderMain 
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
      />  
      )}
      <div className={styles.clinicDetail}>
        {/* 병원 이미지 및 이름 섹션 */}
        <section className={styles.imageSection}>
          <div className={styles.bannerContainer}>
            <button className={styles.prevButton} onClick={handlePrevBanner}>
              &#8249;
            </button>
            <img
              src={bannerImages[currentBannerIndex]}
              alt={name || 'Clinic'}
              className={styles.mainImage}
            />
            <button className={styles.nextButton} onClick={handleNextBanner}>
              &#8250;
            </button>
          </div>
          <div className={styles.clinicInfo}>
            <p className={styles.hospitalAddress}>{nearest_subway_station}</p>
            <h1 className={styles.clinicName}>
              {name}
            </h1>
            <div className={styles.additionalInfo}>
              {/* <img src={detailStar} alt="Rating" className={styles.icon} />
              {rating && (
                <span className={styles.ratingText}>
                  {rating.avg_score.toFixed(1)} ({rating.total_review_quantity})
                </span>
              )} */}
              <img src={detailPhone} alt="Phone" className={styles.icon} />
              <span>
                {hospitalData.tel && ` ${hospitalData.tel} | `}
                {phone}
              </span>
            </div>
            <div className={styles.languageTags}>
              <span>{i18n.t('clinicDetail.header.languages.0')}</span>
              <span>{i18n.t('clinicDetail.header.languages.1')}</span>
              <span>{i18n.t('clinicDetail.header.languages.2')}</span>
              <span>{i18n.t('clinicDetail.header.languages.3')}</span>
            </div>
          </div>
        </section>
        <div className={styles.thickDivider} />

        {/* 병원 소개 섹션 */}
        <section className={styles.section}>
          <h2>{i18n.t('clinicDetail.introduction.title')}</h2>
          <p>{stripHtmlTags(introduce)}</p>
        </section>
        <div className={styles.divider} />

        {/* 병원 주소 섹션 */}
        <section className={styles.section}>
          <h2>{i18n.t('clinicDetail.address.title')}</h2>
          <p>{address}</p>
        </section>
        <div className={styles.divider} />

        {/* 운영 시간 섹션 */}
        <section className={styles.section}>
          <h2>{i18n.t('clinicDetail.operatingHours.title')}</h2>
          <table className={styles.scheduleTable}>
            <thead>
              <tr>
                {daysOfWeek.map((day, index) => (
                  <th
                  key={day}
                  style={
                    index === 6
                      ? { color: "red" }
                      : index === 5
                      ? { color: "blue" }
                      : {}
                  }
                >
                  {day}
                </th>                
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {daysOfWeek.map((day) => (
                  <td key={day}>{renderSchedule(day)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </section>
        <div className={styles.divider} />

        {/* 추가 내용 섹션 */}
        <section className={styles.section}>
          <h2>{i18n.t('clinicDetail.additionalNotes.title')}</h2>
          <p>{i18n.t('clinicDetail.additionalNotes.description')}</p>
        </section>
        <div className={styles.divider} />

        {!isChatModalOpen && !isPaymentModalOpen && (
          <section className={styles.chatContainer}>
          <div className={styles.chatContent}>
          <p className={styles.chatText}>
            {consultCount === null ? (
                <p className={styles.chatText}>...</p>
              ) : (
                <p className={styles.chatText}>
                  {t('clinicDetail.consultCount', { count: consultCount })}
                </p>
            )}
          </p>
            <div className={styles.chatButtons}>
              <button
                className={styles.chatButton}
                onClick={() => setIsChatModalOpen(true)}
              >
                {t('treatmentDetail.clinicInfo.chatWithClinic')}
              </button>
              {/* <button
                className={styles.paymentButton}
                onClick={() => setIsPaymentModalOpen(true)}
              >
                {t('treatmentDetail.clinicInfo.makePayment')}
              </button> */}
            </div>
          </div>
        </section>
        )}
      </div>
      <Footer />
    </>
  );
}

export default ClinicDetail;
